import { capitalize } from 'lodash';

import { formatPhoneNumber } from 'lib/helpers/utility';

import StepInfoCell from '../components/StepInfoCell';
import TableActions from '../components/TableActions';
import GoLiveDateCell from '../components/GoLiveDateCell';
import WizardProgressBar from '../components/WizardProgressBar';
import PracticeStatusCell from '../components/PracticeStatusCell';

const PRACTICE_STATUSES = [
  {
    label: 'Active',
    value: 'active'
  },
  {
    label: 'Inactive',
    value: 'inactive'
  },
  {
    label: 'Pending',
    value: 'pending'
  }
];

const PRACTICE_REGIONS = [
  {
    label: 'East Coast',
    value: 'East Coast'
  },
  {
    label: 'West Coast',
    value: 'West Coast'
  }
];

export const FILTERS_DEFS = {
  id: {
    title: 'Practice ID',
    values: null,
    allowed_operators: ['contains', 'does not contain'],
    icon: 'text-num-mono'
  },
  practice: {
    queryKey: 'practices.options',
    title: 'Practice',
    placeholder: 'Search practice',
    icon: 'user-mono',
    allowed_operators: ['is', 'is not', 'is any of']
  },
  phone: {
    title: 'Phone',
    values: null,
    allowed_operators: ['contains', 'does not contain'],
    icon: 'call-calling-mono'
  },
  timezone: {
    title: 'Timezone',
    values: null,
    allowed_operators: ['contains', 'does not contain'],
    icon: 'timezone-mono'
  },
  status: {
    title: 'Status',
    placeholder: 'Search by status',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: PRACTICE_STATUSES.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'claim_statuses.options',
    icon: 'status-mono'
  },
  region: {
    title: 'Region',
    placeholder: 'Search by region',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: PRACTICE_REGIONS.filter((region) =>
          region.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'practice_regions.options',
    icon: 'location-mono'
  }
};

export const COLUMN_DEFS = [
  { field: 'id', headerName: 'ID', maxWidth: 100 },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 300,
    valueFormatter: ({ value }) => capitalize(value)
  },
  {
    field: 'dba_name',
    headerName: 'DBA',
    minWidth: 200
  },
  {
    field: 'phone',
    headerName: 'Phone',
    minWidth: 170,
    valueFormatter: ({ value }) => formatPhoneNumber(value)
  },
  {
    field: 'timezone',
    headerName: 'Timezone',
    valueFormatter: ({ value }) => capitalize(value)
  },
  {
    field: 'region',
    headerName: 'Region'
  },
  {
    field: 'status',
    headerName: 'Status',
    cellRenderer: ({ value }) => <PracticeStatusCell status={value} />
  },
  {
    field: 'wizard',
    headerName: 'Go-live date',
    minWidth: 200,
    cellRenderer: ({ value }) => <GoLiveDateCell value={value} />
  },
  {
    field: 'wizard',
    headerName: 'Progress',
    minWidth: 200,
    cellRenderer: ({ value }) => <WizardProgressBar value={value?.progress_percent} />
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Support & contact',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__support_and_contact" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Go-live & training dates',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__go_live_dates_and_trainings" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Acknowledgements',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__acknowledgements" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Practice setup',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__basic_setup_confirmation" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Staff',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__staff_and_permissions" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Delegation',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__delegation" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Data import',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__data_import" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'E-prescribe setup',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__e_prescribe_setup" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Myriad billing',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__clearinghouse" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Schedule of fees',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__schedule_of_fees" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Custom forms',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__custom_forms" />
    )
  },
  {
    field: 'wizard',
    minWidth: 150,
    headerName: 'Kiosk setup',
    cellRenderer: ({ value }) => (
      <StepInfoCell value={value} stepDefinition="onboarding__kiosk_setup" />
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClass: 'no-border',
    cellRenderer: (params) => {
      return <TableActions {...params} />;
    },
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const navigateToVistProfile = (navigate, id) => {
  navigate(`/admin/practitioners/${id}`);
};
